import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { fetchOwnClientProfile, updateOwnClientProfile } from '@/api/clients/clientsApi';
import type { Client, ClientProfilePayload } from '@/api/clients/clientsApi.types';
import {
  captureOwnConsents,
  fetchOwnConsents,
  withdrawOwnConsent,
} from '@/api/consents/consentsApi';
import type { ConsentCaptureItem, ConsentStatusItem } from '@/api/consents/consentsApi.types';
import {
  OWN_CLIENT_PROFILE_QUERY_KEY,
  OWN_CONSENTS_QUERY_KEY,
  OWN_PORTAL_SUMMARY_QUERY_KEY,
} from '@/constants/queryKeys';

export function useOwnClientProfileQuery(): UseQueryResult<Client, Error> {
  return useQuery({
    queryKey: OWN_CLIENT_PROFILE_QUERY_KEY,
    queryFn: fetchOwnClientProfile,
  });
}

export function useUpdateOwnProfileMutation(): UseMutationResult<
  Client,
  Error,
  ClientProfilePayload
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateOwnClientProfile,
    onSuccess: function done(client) {
      queryClient.setQueryData(OWN_CLIENT_PROFILE_QUERY_KEY, client);
      void queryClient.invalidateQueries({ queryKey: OWN_PORTAL_SUMMARY_QUERY_KEY });
    },
  });
}

export function useOwnConsentsQuery(): UseQueryResult<ConsentStatusItem[], Error> {
  return useQuery({
    queryKey: OWN_CONSENTS_QUERY_KEY,
    queryFn: fetchOwnConsents,
  });
}

export function useCaptureOwnConsentsMutation(): UseMutationResult<
  ConsentStatusItem[],
  Error,
  ConsentCaptureItem[]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: captureOwnConsents,
    onSuccess: function done(items) {
      queryClient.setQueryData(OWN_CONSENTS_QUERY_KEY, items);
      void queryClient.invalidateQueries({ queryKey: OWN_PORTAL_SUMMARY_QUERY_KEY });
    },
  });
}

export function useWithdrawOwnConsentMutation(): UseMutationResult<
  ConsentStatusItem[],
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: withdrawOwnConsent,
    onSuccess: function done(items) {
      queryClient.setQueryData(OWN_CONSENTS_QUERY_KEY, items);
      void queryClient.invalidateQueries({ queryKey: OWN_PORTAL_SUMMARY_QUERY_KEY });
    },
  });
}
